import { Bounce } from "react-toastify";

/**
 * Route paths used by the Notifications tabs and sidebar sub-options.
 * @readonly
 * @type {{ OUTBOX: string, TEST_EMAIL_TEMPLATE: string, NOTIFICATIONS_ROOT: string }}
 */
export const TEST_EMAIL_TEMPLATE_ROUTE_PATHS = Object.freeze({
    OUTBOX: "/notifications/outbox",
    TEST_EMAIL_TEMPLATE: "/notifications/test-email-template",
    NOTIFICATIONS_ROOT: "/notifications",
});

/**
 * React Router child segment keys for the two Notifications sub-routes.
 * @readonly
 * @enum {string}
 */
export const TEST_EMAIL_TEMPLATE_TAB_KEYS = Object.freeze({
    OUTBOX: "outbox",
    TEST_EMAIL_TEMPLATE: "test-email-template",
});

/**
 * Root segments for TanStack Query keys scoped to the Test Email Template feature.
 * Full keys are built as `[ROOT, …]` to enable safe prefix-based invalidation.
 * @readonly
 * @type {{ TEMPLATE_LIST_ROOT: string, TEMPLATE_VARIABLES_ROOT: string }}
 */
export const TEST_EMAIL_TEMPLATE_QUERY_KEYS = Object.freeze({
    TEMPLATE_LIST_ROOT: "test-email-template-list",
    TEMPLATE_VARIABLES_ROOT: "test-email-template-variables",
});

/**
 * Hardcoded sender identity sent on every direct-email request.
 * These are the only client-side source of truth for sender values — never read from user state.
 * @readonly
 * @type {{ ID: string, EMAIL: string }}
 */
export const HARDCODED_SENDER = Object.freeze({
    ID: "agent-123",
    EMAIL: "No_Reply@centivo.com",
});

/**
 * Email type sent for clarity on every direct-email request.
 * The backend hardcodes this value but the frontend sends it to keep the body self-describing.
 * @constant {string}
 */
export const HARDCODED_REQUEST_TYPE = "EMAIL";

/**
 * Regex for basic email format validation on the `toEmail` field.
 * Accepts any non-whitespace@non-whitespace.non-whitespace pattern.
 * @constant {RegExp}
 */
export const EMAIL_FORMAT_REGEX = /^\S+@\S+\.\S+$/;

/**
 * Fixed sample strings used to pre-fill form fields that do not come from backend metadata.
 * These are sample data only — they carry no metadata authority.
 * @readonly
 * @type {{ MESSAGE: string, FALLBACK_SUBJECT: string, FALLBACK_VARIABLE_VALUE: string }}
 */
export const SAMPLE_DEFAULTS = Object.freeze({
    MESSAGE: "Hello! This is a test email sent from the Business portal to verify that this template renders and dispatches correctly.",
    FALLBACK_SUBJECT: "Test email — please verify template rendering",
    FALLBACK_VARIABLE_VALUE: "Sample",
});

/**
 * Per-variable-name sample values used to pre-fill variable inputs.
 * Keys are the variable names returned by the backend; values are representative sample strings.
 * This map is sample data only — variable names themselves come exclusively from the backend.
 * @readonly
 * @type {Record<string, string>}
 */
export const VARIABLE_NAME_TO_SAMPLE_VALUE = Object.freeze({
    Centivo: "Centivo",
    First_Name: "John",
    Plan_Name: "Focus Plan",
    URL: "https://app.centivo.com",
    Support_Team_Name: "Centivo Support",
    Address_Name: "Member Care",
});

/**
 * Inline validation error messages surfaced under each invalid form field.
 * @readonly
 * @type {{ TEMPLATE_REQUIRED: string, TO_EMAIL_REQUIRED: string, TO_EMAIL_INVALID: string, SUBJECTS_REQUIRED: string, MESSAGE_REQUIRED: string }}
 */
export const VALIDATION_ERROR_MESSAGES = Object.freeze({
    TEMPLATE_REQUIRED: "Template is required",
    TO_EMAIL_REQUIRED: "Recipient email is required",
    TO_EMAIL_INVALID: "Enter a valid email address",
    SUBJECTS_REQUIRED: "Subject is required",
    MESSAGE_REQUIRED: "Message is required",
});

/**
 * User-facing strings for success and error toasts on the send action.
 * @readonly
 * @type {{ SUCCESS: string, FALLBACK_ERROR: string }}
 */
export const TOAST_MESSAGES = Object.freeze({
    SUCCESS: "Email queued for delivery",
    FALLBACK_ERROR: "Failed to send email",
});

/**
 * User-facing error strings shown inline when template-metadata loads fail.
 * @readonly
 * @type {{ TEMPLATES: string, TEMPLATE_VARIABLES: string }}
 */
export const LOAD_FAILURE_MESSAGES = Object.freeze({
    TEMPLATES: "Failed to load email templates. Please try again.",
    TEMPLATE_VARIABLES: "Failed to load template variable details. Please try again.",
});

/**
 * `console.error` prefixes for failed operations (NFR-F4).
 * No PII may follow these prefixes — only status codes and backend-supplied messages.
 * @readonly
 * @type {{ SUBMIT_FAILED: string, TEMPLATES_LOAD_FAILED: string, TEMPLATE_VARIABLES_LOAD_FAILED: string }}
 */
export const OBSERVABILITY_LOG_PREFIXES = Object.freeze({
    SUBMIT_FAILED: "TestEmailTemplate submit failed:",
    TEMPLATES_LOAD_FAILED: "TestEmailTemplate templates load failed:",
    TEMPLATE_VARIABLES_LOAD_FAILED: "TestEmailTemplate template variables load failed:",
});

/**
 * Shared `react-toastify` options for the Test Email Template form.
 * Mirrors `NOTIFICATIONS_PAGE_ERROR_TOAST_OPTIONS` (same Bounce / top-right / 5000 ms config).
 * @type {import("react-toastify").ToastOptions}
 */
export const TEST_EMAIL_TEMPLATE_TOAST_OPTIONS = Object.freeze({
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light",
    transition: Bounce,
});

/**
 * Labels for the Send and Reset action buttons and their state variants.
 * @readonly
 * @type {{ SEND: string, SENDING: string, RESET: string, RETRY: string }}
 */
export const BUTTON_LABELS = Object.freeze({
    SEND: "Send",
    SENDING: "Sending…",
    RESET: "Reset",
    RETRY: "Retry",
});

/**
 * Labels for loading indicator copy inside the variable-inputs block.
 * @readonly
 * @type {{ TEMPLATE_VARIABLES: string }}
 */
export const LOADING_LABELS = Object.freeze({
    TEMPLATE_VARIABLES: "Loading template variables",
});
